/**
 * Toast Notification Composable
 *
 * Provides programmatic API for displaying toast notifications
 *
 * @example
 * import { useToast } from '@/Composables/useToast';
 *
 * const toast = useToast();
 *
 * // Simple usage
 * toast.success('Operation completed successfully!');
 * toast.error('An error occurred');
 * toast.warning('Please check your input');
 * toast.info('New updates available');
 *
 * // With title
 * toast.success('Your changes have been saved', 'Success');
 *
 * // With options
 * toast.success('File uploaded', 'Success', {
 *   duration: 10000, // 10 seconds
 *   action: {
 *     label: 'View',
 *     callback: () => console.log('View clicked')
 *   }
 * });
 *
 * // No auto-dismiss
 * toast.info('Important message', 'Notice', { duration: 0 });
 */

import { ref } from 'vue';

// Global state for toasts (shared across all instances)
const toasts = ref([]);
let nextId = 0;

// Default configuration
const defaultConfig = {
    duration: 5000, // 5 seconds
    position: 'top-right',
};

// Current configuration (can be updated globally)
let globalConfig = { ...defaultConfig };

/**
 * Main composable function
 */
export function useToast() {
    /**
     * Add a toast notification
     * @param {Object} toast - Toast configuration
     * @param {string} toast.type - Type of toast (success, error, warning, info)
     * @param {string} toast.message - Toast message
     * @param {string} [toast.title] - Optional title
     * @param {number} [toast.duration] - Auto-dismiss duration in ms (0 = never)
     * @param {Object} [toast.action] - Optional action button
     * @param {string} toast.action.label - Action button label
     * @param {Function} toast.action.callback - Action button callback
     * @returns {number} Toast ID
     */
    const add = (toast) => {
        const toastId = nextId++;
        const duration = toast.duration !== undefined ? toast.duration : globalConfig.duration;

        const newToast = {
            id: toastId,
            type: toast.type || 'info',
            message: toast.message || '',
            title: toast.title || null,
            action: toast.action || null,
            duration,
            createdAt: Date.now(),
        };

        toasts.value.push(newToast);

        // Auto-dismiss after duration (if duration > 0)
        if (duration > 0) {
            setTimeout(() => {
                remove(toastId);
            }, duration);
        }

        return toastId;
    };

    /**
     * Remove a toast by ID
     * @param {number} toastId - Toast ID to remove
     */
    const remove = (toastId) => {
        const index = toasts.value.findIndex(t => t.id === toastId);
        if (index > -1) {
            toasts.value.splice(index, 1);
        }
    };

    /**
     * Remove all toasts
     */
    const clear = () => {
        toasts.value = [];
    };

    /**
     * Success toast
     * @param {string} message - Toast message
     * @param {string} [title] - Optional title
     * @param {Object} [options] - Additional options
     * @returns {number} Toast ID
     */
    const success = (message, title = null, options = {}) => {
        return add({
            type: 'success',
            message,
            title,
            ...options,
        });
    };

    /**
     * Error toast
     * @param {string} message - Toast message
     * @param {string} [title] - Optional title
     * @param {Object} [options] - Additional options
     * @returns {number} Toast ID
     */
    const error = (message, title = null, options = {}) => {
        return add({
            type: 'error',
            message,
            title,
            duration: options.duration !== undefined ? options.duration : 7000, // Errors stay longer
            ...options,
        });
    };

    /**
     * Warning toast
     * @param {string} message - Toast message
     * @param {string} [title] - Optional title
     * @param {Object} [options] - Additional options
     * @returns {number} Toast ID
     */
    const warning = (message, title = null, options = {}) => {
        return add({
            type: 'warning',
            message,
            title,
            duration: options.duration !== undefined ? options.duration : 6000, // Warnings stay a bit longer
            ...options,
        });
    };

    /**
     * Info toast
     * @param {string} message - Toast message
     * @param {string} [title] - Optional title
     * @param {Object} [options] - Additional options
     * @returns {number} Toast ID
     */
    const info = (message, title = null, options = {}) => {
        return add({
            type: 'info',
            message,
            title,
            ...options,
        });
    };

    /**
     * Promise toast - Shows loading, then success/error based on promise result
     * @param {Promise} promise - Promise to track
     * @param {Object} messages - Messages for different states
     * @param {string} messages.loading - Loading message
     * @param {string} messages.success - Success message
     * @param {string} messages.error - Error message
     * @returns {Promise} Original promise
     */
    const promise = async (promise, messages = {}) => {
        const loadingId = add({
            type: 'info',
            message: messages.loading || 'Loading...',
            duration: 0, // Don't auto-dismiss
        });

        try {
            const result = await promise;
            remove(loadingId);
            success(messages.success || 'Operation completed successfully');
            return result;
        } catch (err) {
            remove(loadingId);
            error(messages.error || err.message || 'An error occurred');
            throw err;
        }
    };

    /**
     * Update global configuration
     * @param {Object} config - Configuration options
     */
    const configure = (config) => {
        globalConfig = { ...globalConfig, ...config };
    };

    /**
     * Get current configuration
     * @returns {Object} Current configuration
     */
    const getConfig = () => {
        return { ...globalConfig };
    };

    return {
        // State
        toasts,

        // Methods
        add,
        remove,
        clear,

        // Shortcuts
        success,
        error,
        warning,
        info,

        // Advanced
        promise,

        // Configuration
        configure,
        getConfig,
    };
}

/**
 * Helper to show Laravel validation errors
 * @param {Object} errors - Laravel validation errors object
 * @param {string} [title] - Optional title
 */
export function showValidationErrors(errors, title = 'Validation Error') {
    const toast = useToast();

    if (typeof errors === 'string') {
        toast.error(errors, title);
        return;
    }

    // If errors is an object with message property
    if (errors.message) {
        toast.error(errors.message, title);
        return;
    }

    // If errors is a validation errors object (key => array of messages)
    const errorMessages = Object.values(errors).flat();

    if (errorMessages.length === 1) {
        toast.error(errorMessages[0], title);
    } else if (errorMessages.length > 1) {
        // Show first error with count
        const message = `${errorMessages[0]} (and ${errorMessages.length - 1} more)`;
        toast.error(message, title, { duration: 7000 });
    }
}

/**
 * Helper to show API errors
 * @param {Error} error - Error object from API
 */
export function showApiError(error) {
    const toast = useToast();

    // Handle different error formats
    if (error.response) {
        // HTTP error response
        const status = error.response.status;
        const data = error.response.data;

        if (status === 422 && data.errors) {
            // Validation errors
            showValidationErrors(data.errors, 'Validation Error');
        } else if (data.message) {
            toast.error(data.message, `Error ${status}`);
        } else {
            toast.error('An unexpected error occurred', `Error ${status}`);
        }
    } else if (error.message) {
        // Network or other error
        toast.error(error.message, 'Error');
    } else {
        toast.error('An unexpected error occurred', 'Error');
    }
}
